import type { AxiosError } from 'axios'

import { gtoWizardAppUrl } from '@gto/urls'

import { getRuntimeConfig, isProdEnv } from '@/config/runtime-env'

export const getAxiosErrorMessage = (error: AxiosError) => {
  // Prioritize `non_field_errors` as they contain the most specific message from the backend.
  if (error.response?.data != null && typeof error.response.data === 'object') {
    const data = error.response.data as Record<string, unknown>
    const nonFieldErrors = data.non_field_errors

    if (Array.isArray(nonFieldErrors) && nonFieldErrors.length > 0) {
      const errorMessage = nonFieldErrors.map((err) => String(err)).join(' ')

      return `Error: ${errorMessage}`
    }
  }

  // todo: handle error more properly to receive
  // error object from tanstack query directly
  const simpleMessage =
    typeof error.response?.data === 'object' &&
    error.response.data !== null &&
    ('detail' in error.response.data ||
      'message' in error.response.data ||
      'error' in error.response.data)
      ? ((error.response.data as { detail?: string; message?: string }).detail ??
        (error.response.data as { message?: string }).message ??
        (error.response.data as { error?: string }).error)
      : undefined

  if (simpleMessage !== undefined && simpleMessage.length > 0) {
    return simpleMessage
  }

  const fieldErrors = Object.entries(error.response?.data ?? {})
    .map((el) => {
      const [field, messages] = el as [string, string[]]

      // Temp solution to correct handle API schema errors
      const fieldToShow = field === 'non_field_errors' ? 'Error' : capitalizeString(field)

      return `${fieldToShow}: ${messages.join(', ')}`
    })
    .join('\n')

  return fieldErrors
}

const capitalizeString = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const safeNumber = (value?: string | number | null): number => {
  const num = Number(value)

  return isNaN(num) ? 0 : num
}

export const oAuthMigrationUrlFlag = 'oauth-migration'

export const buildOAuthRedirectUrl = (backUrl?: string) => {
  const origin = isProdEnv() ? gtoWizardAppUrl : 'https://app.dev.gtowizard.com/'
  const url = new URL(`${origin}login`)

  const redirectUri = new URL(window.location.origin + '/login/oauth')
  // append login type to back url to correctly handle if after returning from oauth provider
  // on our side.
  redirectUri.searchParams.set('type', 'oauth')

  if (backUrl !== undefined && backUrl.length > 0) {
    redirectUri.searchParams.set('back_url', backUrl)
  }

  url.searchParams.set('client_id', getRuntimeConfig().oauthClientId)
  url.searchParams.set('redirect_uri', redirectUri.toString())

  return url.toString()
}

export const getBackUrl = (backUrl?: string) => {
  if (backUrl !== undefined && backUrl !== oAuthMigrationUrlFlag) {
    return backUrl
  }

  return '/app/rewards'
}

type ApiErrorData = { error_code?: string }

export const isErrorCodeInResponse = (data: unknown): data is ApiErrorData => {
  return typeof data === 'object' && data !== null && ('error_code' in data || 'error_code' in data)
}
